import { ICredentials, IEmailVerification, IPasswordResetCredentilas, IEmailSuccess, ICheckTokenUuidCredentials } from '../../../types/auth'
import * as constants from './constants'
import { AuthApi } from '../../../api/authApi'
import { ActionCreator } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { AuthState } from '../reducers'
import { IUserDetail } from '../../../types/user'

export type AuthThunkResult<R> = ThunkAction<Promise<R>, AuthState, null, AuthActions>
export type AuthThunkDispatchType = ThunkDispatch<AuthState, null, AuthActions>

export interface LoginSuccess {
  type: constants.LOGIN_SUCCESS;
  user: IUserDetail;
}

export interface LoginHasErrored {
  type: constants.LOGIN_HAS_ERRORED;
  hasErrored: boolean;
}

export interface GetLoggedInUserSuccess {
  type: constants.GET_LOGGED_IN_USER_SUCCESS;
  user: IUserDetail;
}

export interface LogoutSuccess {
  type: constants.LOGOUT_SUCCESS;
}

export interface EmailResetPassSucceess {
  type: constants.EMAIL_RESET_PWD_SUCCESS,
  msg: IEmailSuccess
}

export interface EmailResetPassHasErrored {
  type: constants.EMAIL_RESET_PWD_HAS_ERRORED
  hasErrord: boolean
}

export interface ResetPasswordRoute {
  type: constants.RESET_PASSWORD_ROUTE,
  route: string
}

export interface SetResetConf {
  type: constants.SET_RESET_CONF,
  resetConf: IPasswordResetCredentilas
}

export interface ResetPasswordSuccess {
  type: constants.RESET_PASSWORD_SUCCESS,
  success: boolean | undefined,
  route: string | undefined
}

export interface ResetPasswordHasError {
  type: constants.RESET_PASSWORD_HAS_ERROR,
  hasErrord: boolean
}

export interface CheckResetTokenSuccess {
  type: constants.CHECK_RESET_TOKEN_SUCCESS,
  checkCredentials: ICheckTokenUuidCredentials
}

export interface CheckResetTokenHasError {
  type: constants.CHECK_RESET_TOKEN_HAS_ERROR,
  hasErrord: boolean
}

export const loginHasErrored = (hasErrored: boolean, error: Error, action?: string): LoginHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.LOGIN_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

// thunk action, with side effect
export const login: ActionCreator<AuthThunkResult<IUserDetail>>
= (credentials: ICredentials): AuthThunkResult<IUserDetail> => {
  return async (dispatch: AuthThunkDispatchType) => {
    return AuthApi.login(credentials)
      .then(response => {
        dispatch(loginSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(loginHasErrored(true, error.response.data, constants.LOGIN))
        return {} as IUserDetail
      })
  }
}

// action, pure function
export const loginSuccess = (user: IUserDetail): LoginSuccess => {
  return {
    type: constants.LOGIN_SUCCESS,
    user
  }
}

export const getLoggedInUser: ActionCreator<AuthThunkResult<IUserDetail>>
= (): AuthThunkResult<IUserDetail> => {
  return async (dispatch: AuthThunkDispatchType) => {
    return AuthApi.loggedInUser()
      .then(response => {
        dispatch(getLoggedInUserSuccess(response))
        return response
      })
      .catch(error => {
        // Only a genuine auth rejection (401/403) may clear the persisted
        // session. A network/CORS/server failure must NOT log a validly
        // authenticated user out on refresh, so it leaves localStorage and
        // Redux state intact. (`error` is undefined when the 403 interceptor
        // already handled the redirect; axios network errors carry no
        // `response`.)
        const response = error && error.response
        const isAuthRejection = !!response && (response.status === 401 || response.status === 403)
        if (isAuthRejection) {
          dispatch(loginHasErrored(true, response.data, constants.GET_LOGGED_IN_USER))
        }
        return {} as IUserDetail
      })
  }
}

export const getLoggedInUserSuccess = (user: IUserDetail): GetLoggedInUserSuccess => {
  return {
    type: constants.GET_LOGGED_IN_USER_SUCCESS,
    user
  }
}

export const logout: ActionCreator<AuthThunkResult<null | void>>
= (): AuthThunkResult<null | void> => {
  return async (dispatch: AuthThunkDispatchType) => {
    return AuthApi.logout()
      .then(response => {
        dispatch(logoutSuccess())
        return response
      })
      .catch(error => {
        dispatch(loginHasErrored(true, error.response.data, constants.LOGOUT))
      })
  }
}

export const logoutSuccess = (): LogoutSuccess => {
  return {
    type: constants.LOGOUT_SUCCESS
  }
}

// RESET PASSWORD PART
export const resetPassword = (route: string): ResetPasswordRoute => {
  return {
    type: constants.RESET_PASSWORD_ROUTE,
    route
  }
}

export const emailVerficationHasError = (hasErrored: boolean, error: Error, action?: string): EmailResetPassHasErrored  => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.EMAIL_RESET_PWD_HAS_ERRORED,
    hasErrord: hasErrored
  }
}

export const resetPasswordEmailVerification: ActionCreator<AuthThunkResult<IEmailSuccess>>
= (email: IEmailVerification): AuthThunkResult<IEmailSuccess> => {
  return async (dispatch: AuthThunkDispatchType) => {
    return AuthApi.emailVerificationResetPwd(email)
      .then(response => {
        dispatch(emailVerificationSuccesss(response))
        return response
      })
      .catch(error => {
        dispatch(emailVerficationHasError(true, error.response.data, constants.EMAIL_RESET_PWD))
        return {} as IEmailSuccess
      })
  }
}

export const emailVerificationSuccesss = (email: IEmailSuccess): EmailResetPassSucceess => {
  toastMsg(email.success, 'EMAIL_REQUEST_SUCCESS', 'success')
  return {
    type: constants.EMAIL_RESET_PWD_SUCCESS,
    msg: email
  }
}

export const setResetConf = (reset: IPasswordResetCredentilas): SetResetConf => {
  return {
    type: constants.SET_RESET_CONF,
    resetConf: reset
  }
}

export const resetPasswordHasError = (hasErrored: boolean, error: Error, action?: string): ResetPasswordHasError  => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.RESET_PASSWORD_HAS_ERROR,
    hasErrord: hasErrored
  }
}

export const resetPasswordSuccess = (success: IPasswordResetCredentilas): ResetPasswordSuccess => {
  if (success.message) {
    toastMsg(success['message'], 'RESET_PASSWORD_SUCCESS', 'success')
  }
  return {
    type: constants.RESET_PASSWORD_SUCCESS,
    success: success.sucesses,
    route: '/login'
  }
}

export const makeNewPassword: ActionCreator<AuthThunkResult<IPasswordResetCredentilas>>
= (passConf: IPasswordResetCredentilas): AuthThunkResult<IPasswordResetCredentilas> => {
  return async (dispatch: AuthThunkDispatchType) => {
    return AuthApi.resetPassword(passConf)
      .then(response => {
        dispatch(resetPasswordSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(resetPasswordHasError(true, error.response.data, constants.RESET_PASSWORD))
        return {} as IPasswordResetCredentilas
      })
  }
}

export const checkTokesSuccess = (checkCredentials: ICheckTokenUuidCredentials): CheckResetTokenSuccess => {
  return {
    type: constants.CHECK_RESET_TOKEN_SUCCESS,
    checkCredentials: checkCredentials
  }
}

export const checkTokenHasErrored = (hasErrored: boolean, error: Error, action?: string): CheckResetTokenHasError  => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.CHECK_RESET_TOKEN_HAS_ERROR,
    hasErrord: hasErrored
  }
}


export const checkResetToken: ActionCreator<AuthThunkResult<ICheckTokenUuidCredentials>>
= (resetToken: ICheckTokenUuidCredentials): AuthThunkResult<ICheckTokenUuidCredentials> => {
  return async (dispatch: AuthThunkDispatchType) => {
    return AuthApi.checkResetToken(resetToken)
      .then(response => {
        dispatch(checkTokesSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(checkTokenHasErrored(true, error.response.data, constants.CHECK_RESET_TOKEN))
        return {} as ICheckTokenUuidCredentials
      })
  }
}

export type AuthActions
  = LoginSuccess
  | LoginHasErrored
  | GetLoggedInUserSuccess
  | LogoutSuccess
  | ResetPasswordRoute
  | EmailResetPassSucceess
  | EmailResetPassHasErrored
  | SetResetConf
  | ResetPasswordSuccess
  | ResetPasswordHasError
  | CheckResetTokenSuccess
  | CheckResetTokenHasError;
